import { Title } from "@components";
import styles from "@styles/pages/reports/reports.module.scss";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Currency } from "shared/utils/Currency";
import { Numbers } from "shared/utils/Numbers";
import classNames from "classnames/bind";
const css = classNames.bind(styles);

export const months = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

export const getGrowthPercentage = ({
  data,
  selectedFilter,
  selectedMonth,
  selectedYear,
}) => {
  // console.log(data, selectedFilter, selectedMonth, selectedYear);

  if (!Array.isArray(data)) return null;

  const today = dayjs();

  const referenceDate =
    selectedFilter === "year"
      ? dayjs(`${selectedYear?.id || today.year()}-12-31`)
      : dayjs(`${selectedMonth?.year}-${selectedMonth?.month}-01`);

  const isCurrentYear = selectedYear?.id === today.year();
  const isCurrentMonth =
    selectedMonth?.year === today.year() &&
    Number(selectedMonth?.month) === today.month() + 1;

  const maxIndex =
    selectedFilter === "year"
      ? isCurrentYear
        ? today.month()
        : 11
      : isCurrentMonth
      ? today.date() - 1
      : dayjs(referenceDate).daysInMonth() - 1;

  const currentIndex = Math.min(maxIndex, data.length - 1);

  const startRaw = data.find(
    (item) =>
      (item?.value || item) !== null &&
      (item?.value || item) !== undefined &&
      (item?.value || item) !== "" &&
      Number(item?.value || item) !== 0
  );

  const startValue = Number(startRaw?.value || startRaw) || 0;

  const rawEnd = [...data]
    .slice(0, currentIndex + 1)
    .reverse()
    .find(
      (item) =>
        (item?.value || item) !== null &&
        (item?.value || item) !== undefined &&
        (item?.value || item) !== "" &&
        Number(item?.value || item) !== 0
    );

  const endValue = Number(rawEnd?.value || rawEnd) || 0;

  // console.log("startValue", startValue, "endValue", endValue);

  if (isNaN(endValue)) return null;

  if (startValue === 0) return endValue === 0 ? 0 : 100;

  const growth = ((endValue - startValue) / startValue) * 100;
  return growth;
};

export const getCurrentMonthYear = () => {
  const today = new Date();

  const date = new Date(today.getFullYear(), today.getMonth());
  const mes = months[date.getMonth()];
  const ano = date.getFullYear();

  return {
    name: `${mes} ${ano}`,
    id: `${date.getMonth() + 1}`,
  };
};

export const getPreviousMonth = () => {
  const today = new Date();

  const date = new Date(today.getFullYear(), today.getMonth() - 1);
  const mes = months[date.getMonth()];
  const ano = date.getFullYear();

  return {
    name: `${mes} ${ano}`,
    id: `${date.getMonth() + 1}`,
  };
};

export const getAllMonthsUntilNow = (
  includeCurrent = true,
  startOnMonth,
  startOnYear,
  limit = 6
) => {
  const endDate = includeCurrent ? dayjs() : dayjs().subtract(1, "month");

  const startDate =
    startOnMonth && startOnYear
      ? dayjs(`${startOnYear}-${String(startOnMonth).padStart(2, "0")}-01`)
      : endDate.subtract(limit - 1, "month");

  const result = [];
  let cursor = endDate;

  while (cursor.isSameOrAfter(startDate, "month") && result.length < limit) {
    result.push({
      name: `${months[cursor.month()]} ${cursor.year()}`,
      year: cursor.year(),
      month: String(cursor.month() + 1).padStart(2, "0"),
      id: String(cursor.month() + 1).padStart(2, "0"),
    });

    cursor = cursor.subtract(1, "month");
  }

  return result;
};

export const getLastSixYears = (startOn2025 = false) => {
  const currentYear = new Date().getFullYear();
  const result = [];

  for (let i = 0; i < 6; i++) {
    const year = currentYear - i;

    if (startOn2025 && year < 2025) continue;

    result.push({
      name: year,
      id: year,
    });
  }

  return result;
};

export const formatByType = (value, type) => {
  if (type === "currency") return Currency.formatCurrency(+value);
  if (type === "int") return Numbers.formatInt(+value);
  if (type === "float") return Numbers.formatFloat(+value);
  if (type === "percentage") return Numbers.percent(+value);

  return value;
};

export const formatNumberWithOneDecimal = (value) => {
  const num = Number(value);
  if (isNaN(num)) return value;
  const rounded = Math.round(num * 10) / 10;
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
};

export const Trend = ({ growth, tooltip, invertColorSign, design = "one" }) => {
  if (growth === null || growth === undefined) return;
  const upColor = invertColorSign ? "#e48186" : "#51D9A3";
  const downColor = invertColorSign ? "#51D9A3" : "#e48186";
  const bgUpColor = invertColorSign
    ? "rgba(216, 90, 96, 0.08)"
    : "rgba(81, 217, 163, 0.08)";
  const bgDownColor = invertColorSign
    ? "rgba(81, 217, 163, 0.08)"
    : "rgba(216, 90, 96, 0.08)";
  const sign = growth > 0 ? "+" : "";
  const isNegative = growth < 0;
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isNegative ? downColor : upColor;
  const bgColor = isNegative ? bgDownColor : bgUpColor;

  if (growth === 0) {
    return (
      <div className={`${styles.growth} ${styles.zero}`} title={tooltip}>
        <div className="d-flex align-center gap-5">
          <p className={styles.growthValue}>0%</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={css({ growth: true, [design]: true })}
      style={{ background: bgColor }}
      title={tooltip}
    >
      <div className="d-flex align-center gap-5">
        <p className={styles.growthValue} style={{ color: iconColor }}>
          {`${sign}${Numbers.formatFloat(growth)}%`}
        </p>
        <i className={iconClass} style={{ color: iconColor }} />
      </div>
    </div>
  );
};

export default function Reports() {
  const pages = [
    {
      name: "Clientes identificados",
      icon: <i className="fal fa-user" />,
      path: "clientes-identificados",
      enabled: true,
    },
    {
      name: "Perfis RFV",
      icon: <i className="fal fa-medal" />,
      path: "perfis-rfv",
      enabled: true,
    },
    {
      name: "Ciclo de Vida",
      icon: <i className="fal fa-hand-holding-dollar" />,
      path: "ciclo-de-vida",
      enabled: true,
    },
  ];

  return (
    <>
      <Title title="Relatórios de Clientes" />

      <div className={styles.reports}>
        {pages
          .filter((p) => !!p.enabled)
          .map((page, index) => {
            return (
              <Link to={page.path} key={index}>
                <div className={styles.report} style={{ opacity: 1 }}>
                  <div className={styles.icon}>{page.icon}</div>
                  <p>{page.name}</p>
                </div>
              </Link>
            );
          })}
      </div>
    </>
  );
}
